// AdministrationTenant — modifier un tenant EXISTANT, sans CC.
//
// 🔑 N'IMPLÉMENTE RIEN. Il câble des endpoints qui existent tous :
//   hubs          PATCH /api/platform/tenants/{id}                 (hubs_actifs)
//   modules       POST  /api/platform/tenants/{id}/modules/{h}/{m}/enable|disable
//   capabilities  POST  /api/platform/features/{slug}/toggle
//   utilisateurs  PUT   /api/hub/users/{id}                        (rôle)
//   séquences     lecture seule — voir la note dans l'onglet
//
// 🔴 DEC-068 — l'écriture des capabilities passe par `/toggle`, qui LIT l'objet
//    complet, mute UNE clé en Python, puis RÉÉCRIT L'OBJET ENTIER. ⛔ Aucun
//    chemin pointé : une clé comme `commercial.aides` contient un point, et un
//    `$set` pointé y créerait un objet imbriqué parasite au lieu de la clé plate.
//    Vérifié dans `feature_flags.toggle_feature` avant de câbler.
//
// 🔴 RÉCAPITULATIF AVANT VALIDATION — rien n'est écrit tant qu'il n'est pas
//    confirmé. ⛔ Pas un second récapitulatif : même forme et même langage que
//    l'assistant de création (AssistantEspaceTravail).
import { useState, useEffect, useCallback, useMemo } from "react";
import { X, Check, AlertTriangle, Loader2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { enteteTenant } from "../../../utils/enteteTenant";
import ProfilMetierTenant from "./ProfilMetierTenant";
import MentionsProspectionTenant from "./MentionsProspectionTenant";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

const ONGLETS = ["Hubs", "Modules", "Capacités", "Utilisateurs", "Séquences", "Profil métier", "Mentions de prospection"];

export default function AdministrationTenant({ tenant, hubsCatalogue = [], onFerme, onApplique }) {
  const slug = tenant?.slug;
  const tid = tenant?.id || tenant?._id;
  const [onglet, setOnglet] = useState(0);
  const [capsCatalogue, setCapsCatalogue] = useState([]);
  const [capsTenant, setCapsTenant] = useState({});
  const [users, setUsers] = useState([]);
  const [seqs, setSeqs] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [recap, setRecap] = useState(false);

  // État VOULU, tenu en mémoire. ⛔ Rien n'est écrit avant confirmation.
  const [voulu, setVoulu] = useState({ hubs: [], modules: {}, caps: {}, roles: {} });
  const [initial, setInitial] = useState({ hubs: [], modules: {}, caps: {}, roles: {} });

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const [rc, rf, ru, rs] = await Promise.all([
        fetch(`${API}/api/platform/features/available`, { headers: auth(), credentials: "include" }),
        fetch(`${API}/api/platform/features/${slug}`, { headers: auth(), credentials: "include" }),
        fetch(`${API}/api/hub/users`, { headers: auth(), credentials: "include" }),
        fetch(`${API}/api/sequences`, { headers: { ...auth(), ...enteteTenant(slug) }, credentials: "include" }),
      ]);
      const cat = rc.ok ? await rc.json() : [];
      setCapsCatalogue(Array.isArray(cat) ? cat : cat.features || []);
      const f = rf.ok ? (await rf.json()).features || {} : {};
      setCapsTenant(f);
      const us = ru.ok ? await ru.json() : [];
      const duTenant = (Array.isArray(us) ? us : us.users || [])
        .filter((u) => (u.tenants || []).includes(slug) || u.current_tenant === slug);
      setUsers(duTenant);
      setSeqs(rs.ok ? await rs.json() : []);

      const mods = {};
      for (const [h, m] of Object.entries(tenant?.modules || {})) {
        for (const [mid, cfg] of Object.entries(m || {})) {
          mods[`${h}.${mid}`] = Boolean(cfg?.enabled ?? cfg);
        }
      }
      const roles = Object.fromEntries(duTenant.map((u) => [u._id || u.user_id, u.role]));
      const etat = { hubs: [...(tenant?.hubs_actifs || [])], modules: mods, caps: { ...f }, roles };
      setVoulu(etat);
      setInitial(JSON.parse(JSON.stringify(etat)));
    } catch (e) {
      setErreur(e.message || "Chargement impossible");
    } finally {
      setChargement(false);
    }
  }, [slug, tenant]);

  useEffect(() => { if (slug) charger(); }, [slug, charger]);

  // Le DELTA — c'est lui que le récapitulatif montre, ⛔ pas l'état complet.
  const delta = useMemo(() => {
    const d = { hubs: [], modules: [], caps: [], roles: [] };
    for (const h of new Set([...initial.hubs, ...voulu.hubs])) {
      const a = initial.hubs.includes(h), b = voulu.hubs.includes(h);
      if (a !== b) d.hubs.push({ id: h, de: a, vers: b });
    }
    for (const k of new Set([...Object.keys(initial.modules), ...Object.keys(voulu.modules)])) {
      if (Boolean(initial.modules[k]) !== Boolean(voulu.modules[k]))
        d.modules.push({ id: k, de: Boolean(initial.modules[k]), vers: Boolean(voulu.modules[k]) });
    }
    for (const k of new Set([...Object.keys(initial.caps), ...Object.keys(voulu.caps)])) {
      if (Boolean(initial.caps[k]) !== Boolean(voulu.caps[k]))
        d.caps.push({ id: k, de: Boolean(initial.caps[k]), vers: Boolean(voulu.caps[k]) });
    }
    for (const k of Object.keys(voulu.roles)) {
      if (initial.roles[k] !== voulu.roles[k])
        d.roles.push({ id: k, de: initial.roles[k], vers: voulu.roles[k] });
    }
    return d;
  }, [initial, voulu]);

  const nbChangements = delta.hubs.length + delta.modules.length + delta.caps.length + delta.roles.length;

  const appliquer = useCallback(async () => {
    setEnCours(true); setErreur(null);
    const echecs = [];
    try {
      if (delta.hubs.length) {
        const r = await fetch(`${API}/api/platform/tenants/${tid}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", ...auth() },
          credentials: "include",
          body: JSON.stringify({ hubs_actifs: voulu.hubs }),
        });
        if (!r.ok) echecs.push(`hubs (HTTP ${r.status})`);
      }
      for (const m of delta.modules) {
        const [hub, mod] = m.id.split(".");
        const r = await fetch(
          `${API}/api/platform/tenants/${tid}/modules/${hub}/${mod}/${m.vers ? "enable" : "disable"}`,
          { method: "POST", headers: auth(), credentials: "include" });
        if (!r.ok) echecs.push(`module ${m.id} (HTTP ${r.status})`);
      }
      for (const c of delta.caps) {
        // 🔴 `/toggle` réécrit l'objet ENTIER — ⛔ pas de chemin pointé (DEC-068).
        const r = await fetch(`${API}/api/platform/features/${slug}/toggle`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...auth() },
          credentials: "include",
          body: JSON.stringify({ feature_id: c.id, enabled: c.vers, updated_by: "admin_tenant_ui" }),
        });
        if (!r.ok) echecs.push(`capacité ${c.id} (HTTP ${r.status})`);
      }
      for (const rl of delta.roles) {
        const r = await fetch(`${API}/api/hub/users/${rl.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...auth() },
          credentials: "include",
          body: JSON.stringify({ role: rl.vers }),
        });
        if (!r.ok) echecs.push(`rôle ${rl.id} (HTTP ${r.status})`);
      }
      if (echecs.length) {
        // ⛔ On ne dit PAS « appliqué » quand une partie a échoué.
        setErreur(`${echecs.length} changement(s) en échec : ${echecs.join(" · ")}`);
        toast.error(`${echecs.length} changement(s) en échec`);
      } else {
        toast.success(`${nbChangements} changement(s) appliqué(s) à ${slug}`);
        onApplique?.();
      }
    } catch (e) {
      setErreur(e.message || "Application impossible");
    } finally {
      setEnCours(false);
      setRecap(false);
      charger();
    }
  }, [delta, tid, slug, voulu, nbChangements, onApplique, charger]);

  const puce = (actif, texte, onClick, testid, disabled = false) => (
    <button key={texte} type="button" onClick={onClick} disabled={disabled}
      data-testid={testid} aria-label={`${actif ? "Désactiver" : "Activer"} ${texte}`}
      className="px-3 py-1.5 rounded-lg text-sm text-left"
      style={{
        background: actif ? "var(--oh-stat-green-bg)" : "var(--oh-bg-surface)",
        color: actif ? "var(--oh-stat-green-text)" : "var(--oh-text-secondary)",
        border: `0.5px solid ${actif ? "var(--oh-stat-green-border)" : "var(--oh-border)"}`,
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? "default" : "pointer",
      }}>
      {actif ? "✓ " : "○ "}{texte}
    </button>
  );

  const parHub = useMemo(() => {
    const g = {};
    for (const c of capsCatalogue) (g[c.parent_hub || "autres"] = g[c.parent_hub || "autres"] || []).push(c);
    return g;
  }, [capsCatalogue]);

  if (!slug) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.55)" }}>
      <div className="w-full max-w-3xl rounded-xl flex flex-col"
           style={{ background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)", maxHeight: "88vh" }}
           data-testid="administration-tenant">
        <div className="flex items-center justify-between p-4" style={{ borderBottom: "0.5px solid var(--oh-border)" }}>
          <div>
            <h2 className="text-base" style={{ color: "var(--oh-text)", fontWeight: 500 }}>
              Modifier « {tenant?.nom || slug} »
            </h2>
            <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
              {nbChangements === 0 ? "Aucun changement en attente"
                : `${nbChangements} changement(s) en attente — rien n'est écrit avant confirmation`}
            </p>
          </div>
          <button type="button" onClick={onFerme} aria-label="Fermer" data-testid="admin-fermer"
                  style={{ color: "var(--oh-text-secondary)" }}><X className="w-5 h-5" /></button>
        </div>

        <div className="flex gap-1 px-4 pt-3 flex-wrap">
          {ONGLETS.map((o, i) => (
            <button key={o} type="button" onClick={() => setOnglet(i)}
                    data-testid={`admin-onglet-${i}`}
                    className="px-3 py-1 rounded-lg text-xs"
                    style={{
                      background: i === onglet ? "var(--oh-accent-bg)" : "transparent",
                      color: i === onglet ? "var(--oh-accent-text)" : "var(--oh-text-secondary)",
                    }}>{o}</button>
          ))}
        </div>

        <div className="p-4 overflow-y-auto" style={{ flex: 1 }}>
          {chargement && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--oh-text-muted)" }} /></div>}

          {!chargement && onglet === 0 && (
            <>
              <p className="text-xs mb-3" style={{ color: "var(--oh-text-secondary)" }}>
                Un hub fermé masque toute sa section. ⚠️ Une entrée de menu sans capacité
                déclarée s'affiche dès que sa section s'affiche&nbsp;: fermer le hub est
                la seule garantie.
              </p>
              <div className="flex flex-wrap gap-2">
                {hubsCatalogue.map((h) => puce(
                  voulu.hubs.includes(h.id), h.name,
                  () => setVoulu((p) => ({ ...p, hubs: p.hubs.includes(h.id) ? p.hubs.filter((x) => x !== h.id) : [...p.hubs, h.id] })),
                  `admin-hub-${h.id}`))}
              </div>
            </>
          )}

          {!chargement && onglet === 1 && (
            <>
              <p className="text-xs mb-3" style={{ color: "var(--oh-text-secondary)" }}>
                Un module désactivé rend ses routes indisponibles — la garde est côté API.
              </p>
              {hubsCatalogue.filter((h) => voulu.hubs.includes(h.id)).map((h) => (
                <div key={h.id} className="mb-3">
                  <div className="text-xs mb-1.5" style={{ color: "var(--oh-text-muted)" }}>{h.name}</div>
                  <div className="flex flex-wrap gap-2">
                    {(h.modules || []).map((m) => puce(
                      m.required || Boolean(voulu.modules[`${h.id}.${m.id}`]),
                      m.name + (m.required ? " (requis)" : ""),
                      () => { if (m.required) return;
                        const k = `${h.id}.${m.id}`;
                        setVoulu((p) => ({ ...p, modules: { ...p.modules, [k]: !p.modules[k] } })); },
                      `admin-module-${h.id}-${m.id}`, m.required))}
                  </div>
                </div>
              ))}
            </>
          )}

          {!chargement && onglet === 2 && (
            <>
              <div className="rounded-lg p-3 mb-3 text-sm"
                   style={{ background: "var(--oh-info-bg)", color: "var(--oh-info-text)", border: "0.5px solid var(--oh-info-border)" }}>
                <strong>{Object.values(voulu.caps).filter(Boolean).length} ouvertes</strong> sur {capsCatalogue.length}.
                ⛔ Une capacité décochée est écrite à <code>False</code>, ⛔ pas laissée absente&nbsp;:
                une clé absente serait interprétée comme autorisée.
              </div>
              {Object.entries(parHub).map(([hub, liste]) => (
                <div key={hub} className="mb-3">
                  <div className="text-xs mb-1" style={{ color: "var(--oh-text-muted)" }}>{hub}</div>
                  <div className="flex flex-wrap gap-2">
                    {liste.map((c) => puce(
                      Boolean(voulu.caps[c.id]), c.name || c.id,
                      () => setVoulu((p) => ({ ...p, caps: { ...p.caps, [c.id]: !p.caps[c.id] } })),
                      `admin-cap-${c.id}`))}
                  </div>
                </div>
              ))}
            </>
          )}

          {!chargement && onglet === 3 && (
            <>
              <p className="text-xs mb-3" style={{ color: "var(--oh-text-secondary)" }}>
                Les utilisateurs rattachés à ce tenant. ⛔ La CRÉATION reste dans l'écran
                Utilisateurs&nbsp;: on ne duplique pas un second gestionnaire.
              </p>
              {users.length === 0 && <div className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Aucun utilisateur rattaché.</div>}
              {users.map((u) => {
                const k = u._id || u.user_id;
                return (
                  <div key={k} className="flex items-center justify-between py-2"
                       style={{ borderBottom: "0.5px solid var(--oh-border)" }}>
                    <div className="text-sm" style={{ color: "var(--oh-text)" }}>
                      {u.name || u.email}
                      <span className="text-xs ml-2" style={{ color: "var(--oh-text-muted)" }}>{u.email}</span>
                    </div>
                    <select value={voulu.roles[k] || u.role || "user"}
                            aria-label={`Rôle de ${u.email}`} data-testid={`admin-role-${k}`}
                            onChange={(e) => setVoulu((p) => ({ ...p, roles: { ...p.roles, [k]: e.target.value } }))}
                            className="px-2 rounded-lg text-sm"
                            style={{ height: 32, background: "var(--oh-bg-surface)", color: "var(--oh-text)", border: "0.5px solid var(--oh-border)" }}>
                      {["admin", "manager", "user", "external", "regie"].map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                );
              })}
            </>
          )}

          {!chargement && onglet === 5 && <ProfilMetierTenant slug={slug} onModifie={onApplique} />}
          {!chargement && onglet === 6 && <MentionsProspectionTenant slug={slug} />}

          {!chargement && onglet === 4 && (
            <>
              <div className="rounded-lg p-3 mb-3 text-sm"
                   style={{ background: "var(--oh-info-bg)", color: "var(--oh-info-text)", border: "0.5px solid var(--oh-info-border)" }}>
                Les compteurs sont en <strong>lecture seule ici</strong>. Changer un préfixe est
                une opération gouvernée&nbsp;: elle demande une prévisualisation, une confirmation,
                et un <strong>verrou dérivé du nombre de documents déjà émis</strong> — ⛔ un
                préfixe déjà porté par des documents opposables ne se change pas d'un clic.
                Cette gouvernance n'est pas encore construite&nbsp;; l'écran le dit plutôt que
                d'offrir un champ dangereux.
              </div>
              {seqs.length === 0 && <div className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Aucune séquence.</div>}
              {(Array.isArray(seqs) ? seqs : []).map((s) => (
                <div key={s.document_type} className="flex justify-between text-sm py-1.5"
                     style={{ borderBottom: "0.5px solid var(--oh-border)", color: "var(--oh-text)" }}>
                  <span>{s.document_type}</span>
                  <span style={{ fontFamily: "monospace", color: "var(--oh-text-secondary)" }}>
                    {s.prefix} · prochain {s.next_number}
                  </span>
                </div>
              ))}
            </>
          )}

          {erreur && (
            <div className="rounded-lg p-3 mt-3 flex gap-2 text-sm"
                 style={{ background: "var(--oh-error-bg)", color: "var(--oh-error-text)", border: "0.5px solid var(--oh-error-border)" }}>
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span>{erreur}</span>
            </div>
          )}
        </div>

        {/* RÉCAPITULATIF — même forme que l'assistant de création */}
        {recap && (
          <div className="p-4 text-sm" style={{ borderTop: "0.5px solid var(--oh-border)", color: "var(--oh-text)" }}
               data-testid="admin-recap">
            <div className="mb-2" style={{ fontWeight: 500 }}>
              {nbChangements} changement(s) — vérifiez avant d'appliquer
            </div>
            {[["Hubs", delta.hubs], ["Modules", delta.modules], ["Capacités", delta.caps], ["Rôles", delta.roles]]
              .filter(([, l]) => l.length).map(([titre, liste]) => (
                <div key={titre} className="mb-2">
                  <div className="text-xs" style={{ color: "var(--oh-text-muted)" }}>{titre}</div>
                  {liste.map((x) => (
                    <div key={x.id} className="text-xs">
                      <code>{x.id}</code> :{" "}
                      <span style={{ color: "var(--oh-text-secondary)" }}>{String(x.de)}</span>
                      {" → "}
                      <strong style={{ color: x.vers === false ? "var(--oh-error-text)" : "var(--oh-stat-green-text)" }}>
                        {String(x.vers)}
                      </strong>
                    </div>
                  ))}
                </div>
              ))}
            <div className="text-xs mt-2" style={{ color: "var(--oh-text-muted)" }}>
              ⚠️ Désactiver retire l'entrée du menu ET fait refuser l'API. Réactiver rétablit les deux.
            </div>
          </div>
        )}

        <div className="flex items-center justify-between p-4" style={{ borderTop: "0.5px solid var(--oh-border)" }}>
          <button type="button" onClick={() => { setVoulu(JSON.parse(JSON.stringify(initial))); setRecap(false); }}
                  disabled={nbChangements === 0} aria-label="Annuler les changements"
                  className="px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"
                  style={{ border: "0.5px solid var(--oh-border)", color: "var(--oh-text-secondary)", opacity: nbChangements ? 1 : 0.4 }}>
            <Undo2 className="w-4 h-4" /> Annuler
          </button>
          {!recap ? (
            <button type="button" onClick={() => setRecap(true)} disabled={nbChangements === 0}
                    aria-label="Voir le récapitulatif" data-testid="admin-recapituler"
                    className="px-4 py-1.5 rounded-lg text-sm"
                    style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)", opacity: nbChangements ? 1 : 0.4 }}>
              Récapituler ({nbChangements})
            </button>
          ) : (
            <button type="button" onClick={appliquer} disabled={enCours}
                    aria-label="Appliquer les changements" data-testid="admin-appliquer"
                    className="px-4 py-1.5 rounded-lg text-sm flex items-center gap-2"
                    style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)", opacity: enCours ? 0.5 : 1 }}>
              {enCours ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Appliquer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
